/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import { UIChartsIcon, UIFeatherIcon, UISwitch } from '@futrli/ui-components';
import classNames from 'classnames';
import React, { useContext, useState } from 'react';
import { noop } from 'lodash';
import { useParams } from 'react-router-dom';
import Tooltip from 'carbon-react/lib/components/tooltip';
import { FormattedMessage, useIntl } from 'react-intl';
import clsx from 'clsx';

import { nestedAccountHeightOverride } from '../style-rows';
import { PaymentTypeRow } from '../types';
import { Actions, AllowedAssumptionTypes } from '../../actions/actions';
import { getRowHeightSize } from '../../user-grid-settings/user-grid-settings-actions';
import { UserGridSettingsContext } from '../../user-grid-settings/user-grid-settings-context';
import { MARGIN_LEFT_LEVEL, isAiAssumption } from './header-row-utils';

import grid from '../../../../../ui/data-grid/components/grid.scss';
import styles from './header-row.scss';
import { getColors } from '../style-rows';
import { PredictionsAccountCategories, VIEW_TYPES } from '../../../../../core/types';
import { extractIdsAndTypes, updatePredictionAndPaymentType } from '../../assumptions/settings/settings-modal-utils';
import { PredictManagementContext } from '../../../../../predict-management/context/predict-management-context';
import { usePredictionsInfoContext } from '../../../context/predictions-info-context';
import { Forecast } from '@futrli-sdk/predict';

interface Props {
  isLast?: boolean;
  level: number;
  row: PaymentTypeRow;
  wrapperStyle: React.CSSProperties;
  category?: PredictionsAccountCategories;
  viewType?: VIEW_TYPES;
  onManualPredictionDrawerOpen: (predictionId?: string, predictionType?: string, isQuickCopy?: boolean) => void;
}

export const HeaderRowPayment: React.FC<Props> = React.memo(
  ({ row, level, isLast, wrapperStyle, category, viewType, onManualPredictionDrawerOpen }) => {
    const [excludedFromReport, setExcludedFromReport] = useState(row.account.excluded_from_report);
    const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
    const workspaceId = tempWorkspaceId || '';
    const organisationId = tempOrganisationId || '';

    const { addErrorMessage } = useContext(PredictManagementContext);

    const { rowHeights, isInEditingMode } = React.useContext(UserGridSettingsContext);
    const {
      forecast,
      fetchForecastReport,
      setIsTogglingPrediction,
      isConsolidation,
      updateAssumption,
      isReadOnly,
      isTogglingPrediction
    } = usePredictionsInfoContext();
    const rowHeight = getRowHeightSize(rowHeights);

    const assumptionsRowHeightOverride = nestedAccountHeightOverride(row, rowHeight, level);

    const inEditingMode = isInEditingMode(forecast?.id || '', row);

    const isRowAiAssumption = isAiAssumption(row);

    const isExcelAssumption = !!row.account.is_excel_assumption;

    const intl = useIntl();

    const headerRowClasses = classNames({
      [grid.rowHeader]: true,
      [grid.isChild]: true,
      [grid.isFirst]: row.siblingIndex === 0,
      [grid.isLast]: isLast,
      [grid.isPrediction]: true,
      [grid.isAiAssumption]: isRowAiAssumption
    });

    const headerRowInnerClasses = classNames({
      [grid.rowHeaderInner]: true,
      [grid[assumptionsRowHeightOverride]]: true
    });

    const colors = getColors(category || 'income', viewType);

    const handleUpdatePrediction = async (data: Partial<AllowedAssumptionTypes>) => {
      const [predictionType, predictionId, paymentType] = extractIdsAndTypes(row.account);

      if (!workspaceId || !organisationId || !forecast?.id || !fetchForecastReport || !addErrorMessage) {
        return addErrorMessage?.(new Error('Missing parameters for updatePredictionAndPaymentType'));
      }

      await updatePredictionAndPaymentType(
        workspaceId,
        organisationId,
        forecast.id,
        predictionType,
        predictionId,
        paymentType,
        {
          prediction: data
        },
        fetchForecastReport,
        addErrorMessage,
        undefined,
        noop,
        noop
      );
    };

    const handleUpdateConsolidatedPrediction = async (data: Partial<AllowedAssumptionTypes>) => {
      if (!workspaceId || !organisationId || !forecast?.id || !updateAssumption) {
        return addErrorMessage?.(new Error('Missing parameters for updateConsolidatedPrediction'));
      }
      await updateAssumption(data, workspaceId, organisationId, forecast.id, row.account.index);
    };

    const handleRowToggle = async () => {
      if (isReadOnly) {
        return;
      }

      setIsTogglingPrediction?.(true);
      setExcludedFromReport(!excludedFromReport);
      if (isConsolidation) {
        await handleUpdateConsolidatedPrediction({ excluded_from_report: !excludedFromReport });
      } else {
        await handleUpdatePrediction({
          excluded_from_report: !excludedFromReport
        });
      }
      setIsTogglingPrediction?.(false);
    };

    const headerTitleClasses = clsx({
      [grid.title]: true,
      [styles.excelNameIcon]: isExcelAssumption
    });

    return (
      <div className={headerRowClasses} style={wrapperStyle} role="row" data-testid="account-header">
        <div
          role="cell"
          className={headerRowInnerClasses}
          style={{
            backgroundColor: getColors(category || 'income', viewType)[2],
            marginLeft: level * MARGIN_LEFT_LEVEL
          }}
        >
          <div className={classNames(grid.flexContainer, grid.flexContainerJustify)}>
            {!isReadOnly && !row.account.is_derived && !(forecast as Forecast)?.is_frozen && (
              <div data-testid="futrli-predicts-toggle" style={{ marginLeft: -20 }}>
                <UISwitch
                  checked={!excludedFromReport}
                  disabled={inEditingMode || isTogglingPrediction}
                  onChange={handleRowToggle}
                  colorThumb={colors[3]}
                  colorTrack={colors[4]}
                />
              </div>
            )}
            <div className={headerTitleClasses} data-testid="account-name">
              {isRowAiAssumption ? (
                <React.Fragment>
                  <UIFeatherIcon iconName="trending-up" svgAttributes={{ className: styles.predictedIcon }} />
                  {row.account.is_derived
                    ? intl.formatMessage(
                        {
                          id: 'predict.common.ai_assumption_name',
                          defaultMessage: 'Predicted ({primaryAccountName})'
                        },
                        { primaryAccountName: row.account.primary_account_name }
                      )
                    : intl.formatMessage({ id: 'predict.common.predicted', defaultMessage: 'Predicted' })}
                </React.Fragment>
              ) : isExcelAssumption ? (
                <React.Fragment>
                  <Tooltip
                    message={
                      <FormattedMessage
                        id="predict.excel_imported_prediction"
                        defaultMessage="Excel imported prediction"
                      />
                    }
                    position="top"
                  >
                    <span style={{ display: 'flex' }}>
                      <UIChartsIcon iconName="mini-excel-icon" />
                    </span>
                  </Tooltip>
                  {row.account.assumption_name}
                </React.Fragment>
              ) : (
                row.account.assumption_name
              )}
            </div>
            {!isReadOnly && !isRowAiAssumption && !isExcelAssumption && (
              <div className={grid.rowHeaderActions}>
                <Actions
                  rowHeight={assumptionsRowHeightOverride}
                  accountName={row.parentName}
                  predictionCount={row.siblingCount}
                  row={row.account}
                  onManualPredictionDrawerOpen={onManualPredictionDrawerOpen}
                />
              </div>
            )}
            {/* <div style={{ width: 72, borderLeft: '1px solid #F6F7FA', height: '100%' }}></div> */}
          </div>
        </div>
      </div>
    );
  }
);
